import * as React from "react";
import { cn } from "@utils";

interface FormattedNumberInputProps {
    id: string;
    name: string;
    value: string | number;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    label?: string;
    helpText?: string;
    decimalPlaces?: number;
    formatFunction?: (value: string | number) => string;
}

const FormattedNumberInput = React.forwardRef<
    HTMLInputElement,
    FormattedNumberInputProps
>(
    (
        {
            id,
            name,
            value,
            onChange,
            placeholder = "",
            required = false,
            disabled = false,
            className = "",
            label,
            helpText,
            decimalPlaces = 2,
            formatFunction,
            ...props
        },
        ref
    ) => {
        const handleFormattedNumberChange = (
            e: React.ChangeEvent<HTMLInputElement>
        ) => {
            const { value: inputValue, selectionStart } = e.target;

            // Store cursor position and count commas before cursor
            const cursorPosition = selectionStart;
            const commasBeforeCursor = (
                inputValue.substring(0, cursorPosition).match(/,/g) || []
            ).length;

            // Remove all non-numeric characters except dots
            let numericValue = inputValue.replace(/[^0-9.]/g, "");

            // Handle multiple dots by keeping only the first one
            const parts = numericValue.split(".");
            if (parts.length > 2) {
                numericValue = parts[0] + "." + parts.slice(1).join("");
            }

            // Limit to specified decimal places
            if (parts.length === 2 && parts[1].length > decimalPlaces) {
                numericValue =
                    parts[0] + "." + parts[1].substring(0, decimalPlaces);
            }

            // Create a synthetic event with the numeric value
            const syntheticEvent = {
                ...e,
                target: {
                    ...e.target,
                    name,
                    value: numericValue,
                },
            };

            // Call the original onChange with the synthetic event
            onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);

            // Restore cursor position after the next render
            setTimeout(() => {
                const input = e.target;
                if (input) {
                    const formattedValue = formatFunction
                        ? formatFunction(numericValue)
                        : formatCurrency(numericValue, decimalPlaces);

                    // Calculate new cursor position accounting for commas
                    const numericPosition = cursorPosition - commasBeforeCursor;
                    let newPosition = 0;
                    let numericCount = 0;

                    for (let i = 0; i < formattedValue.length; i++) {
                        if (formattedValue[i] !== ",") {
                            numericCount++;
                        }
                        if (numericCount >= numericPosition) {
                            newPosition = i + 1;
                            break;
                        }
                    }

                    newPosition = Math.min(newPosition, formattedValue.length);
                    input.setSelectionRange(newPosition, newPosition);
                }
            }, 0);
        };

        const formatCurrency = (value: string | number, places: number = 2) => {
            if (value === null || value === undefined || value === "")
                return "";
            const number = parseFloat(value.toString());
            if (isNaN(number)) return "";
            return new Intl.NumberFormat("en-US", {
                minimumFractionDigits: places,
                maximumFractionDigits: places,
            }).format(number);
        };

        const displayValue = formatFunction
            ? formatFunction(value)
            : formatCurrency(value, decimalPlaces);

        const inputElement = (
            <input
                ref={ref}
                id={id}
                name={name}
                type="text"
                value={displayValue}
                onChange={handleFormattedNumberChange}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                className={cn(
                    "block w-full px-4 py-2 mt-2 text-gray-700 bg-white border border-gray-200 rounded-md focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 focus:outline-none focus:ring",
                    disabled && "bg-gray-100",
                    className
                )}
                {...props}
            />
        );

        if (label) {
            return (
                <div>
                    <label htmlFor={id} className="text-gray-700 font-medium">
                        {label}{" "}
                        {required && <span className="text-red-500">*</span>}
                    </label>
                    {inputElement}
                    {helpText && (
                        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
                    )}
                </div>
            );
        }

        return inputElement;
    }
);

FormattedNumberInput.displayName = "FormattedNumberInput";

export { FormattedNumberInput };
